import React from 'react';
import { AlertTriangle, Clock, Calendar, DollarSign, Users } from 'lucide-react';

interface ContractStats {
  thisSeason: number;
  oneYear: number;
  longTerm: number;
  expired: number;
  totalValue: number;
  totalPlayers: number;
}

interface ContractStatsProps {
  stats: ContractStats;
}

const ContractStatsDashboard: React.FC<ContractStatsProps> = ({ stats }) => {
  const statsCards = [
    {
      title: 'Vencen Esta Temporada',
      value: stats.thisSeason,
      icon: AlertTriangle,
      color: 'red',
      gradient: 'from-red-500 to-red-600',
      bgGradient: 'from-red-50 to-red-100',
      description: 'Contratos críticos'
    },
    {
      title: 'Vencen en 1 Año',
      value: stats.oneYear,
      icon: Clock,
      color: 'orange',
      gradient: 'from-orange-500 to-orange-600',
      bgGradient: 'from-orange-50 to-orange-100',
      description: 'Planificación necesaria'
    },
    {
      title: 'Larga Duración',
      value: stats.longTerm,
      icon: Calendar,
      color: 'green',
      gradient: 'from-green-500 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      description: 'Contratos estables'
    },
    {
      title: 'Total Jugadores',
      value: stats.totalPlayers,
      icon: Users,
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600',
      bgGradient: 'from-blue-50 to-blue-100',
      description: 'En seguimiento'
    },
    {
      title: 'Valor Total',
      value: `€${(stats.totalValue / 1_000_000).toFixed(1)}M`,
      icon: DollarSign,
      color: 'purple',
      gradient: 'from-purple-500 to-purple-600',
      bgGradient: 'from-purple-50 to-purple-100',
      description: 'Valor de mercado'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
      {statsCards.map((stat, index) => {
        const Icon = stat.icon;
        
        return (
          <div
            key={stat.title}
            className={`group relative overflow-hidden bg-gradient-to-br ${stat.bgGradient} rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2 border border-white/50`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-white"></div>
            </div>
            
            {/* Floating icon background */}
            <div className={`absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-br ${stat.gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300`}></div>
            
            <div className="relative z-10">
              {/* Icon and title */}
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-right">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {stat.description}
                  </div>
                </div>
              </div>

              {/* Value */}
              <div className="mb-2">
                <div className={`text-3xl font-bold text-${stat.color}-700 group-hover:text-${stat.color}-800 transition-colors duration-300`}>
                  {stat.value}
                </div>
              </div>

              {/* Title */}
              <div className="text-sm font-semibold text-gray-700 leading-tight">
                {stat.title}
              </div>

              {/* Progress bar for visual impact */}
              <div className="mt-3 h-1 bg-white/30 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${stat.gradient} rounded-full transform origin-left transition-transform duration-1000 group-hover:scale-x-110`}
                  style={{ 
                    width: `${Math.min(100, (typeof stat.value === 'number' ? stat.value / Math.max(...statsCards.map(s => typeof s.value === 'number' ? s.value : 0)) * 100 : 100))}%`
                  }}
                ></div>
              </div>
            </div>

            {/* Hover glow effect */}
            <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 bg-gradient-to-r ${stat.gradient} blur-xl`}></div>
          </div>
        );
      })}
    </div>
  );
};

export default ContractStatsDashboard;